import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// Track navigation clicks
const trackNavClick = async (navLabel: string) => {
  try {
    await supabase.from('cta_clicks').insert({
      cta_type: 'nav_click',
      cta_location: navLabel.toLowerCase(),
      source_url: window.location.pathname,
      session_id: sessionStorage.getItem('session_id') || crypto.randomUUID(),
      user_agent: navigator.userAgent,
    });
  } catch (error) {
    console.error('Failed to track nav click:', error);
  }
};
// Navigation links
const navLinks: { label: string; href: string; isAnchor: boolean; isExternal: boolean }[] = [];

interface TrueWellnessHeaderProps {
  onOpenModal: () => void;
  activeCategory?: number;
  isTransitioning?: boolean;
}

const TrueWellnessHeader = ({ onOpenModal, activeCategory, isTransitioning }: TrueWellnessHeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    const id = href.replace("/#", "");
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-[#181818]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <span className="text-xl md:text-2xl font-black tracking-tight text-white drop-shadow-md">
              Hillary<span className="text-[#ffdb4d]">Rx</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              link.isAnchor ? (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="text-sm font-semibold text-[#ffdb4d] hover:text-[#ffdb4d]/80 drop-shadow-sm transition-colors"
                >
                  {link.label}
                </button>
              ) : link.isExternal ? (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackNavClick(link.label)}
                  className="text-sm font-semibold text-[#ffdb4d] hover:text-[#ffdb4d]/80 drop-shadow-sm transition-colors"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => trackNavClick(link.label)}
                  className="text-sm font-semibold text-[#ffdb4d] hover:text-[#ffdb4d]/80 drop-shadow-sm transition-colors"
                >
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <Button size="sm" className="font-semibold bg-[#ffdb4d] hover:bg-[#ffdb4d]/90 text-black" onClick={onOpenModal}>
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 bg-black/90 backdrop-blur-md rounded-b-2xl mt-2">
            <nav className="flex flex-col gap-4 px-4">
              {navLinks.map((link) => (
                link.isAnchor ? (
                  <button
                    key={link.label}
                    onClick={() => scrollToSection(link.href)}
                    className="text-left text-sm font-semibold text-[#ffdb4d] hover:text-[#ffdb4d]/80 transition-colors"
                  >
                    {link.label}
                  </button>
                ) : link.isExternal ? (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackNavClick(link.label);
                      setIsMenuOpen(false);
                    }}
                    className="text-left text-sm font-semibold text-[#ffdb4d] hover:text-[#ffdb4d]/80 transition-colors"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => {
                      trackNavClick(link.label);
                      setIsMenuOpen(false);
                    }}
                    className="text-left text-sm font-semibold text-[#ffdb4d] hover:text-[#ffdb4d]/80 transition-colors"
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <div className="flex flex-col gap-2 pt-4 border-t border-white/20">
                <Button size="sm" className="w-full font-semibold bg-[#ffdb4d] hover:bg-[#ffdb4d]/90 text-black" onClick={onOpenModal}>
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default TrueWellnessHeader;